import React, { useState } from 'react';
import moment from 'moment';
import Icon from 'src/components/Icon/Icon';
import { observer } from 'src/utils/mobx-react';
import { FreeBetsFormState } from 'src/components/FreeBetsForm/FreeBetsForm.state';
import { useAppStateContext } from 'src/AppState/AppState';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import {
    ButtonContainer,
    ButtonContiner,
    ErrorMessage,
    SuccessMessage,
} from 'src/components/FreeBetsForm/FreeBetsForm.style';
import { DateAdditions } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/DateAdditions';
import { PaymentAdditionsNew } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/PaymentsAdditionsNew';
import { Amount } from 'src_common/common/amount/Amount';
import { CurrencyType, formatAmountWithCurrency } from 'src_common/common/amount/website-money/currency';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { useCommon } from 'src/AppState/Common';

const defaultDateFormat = 'YYYY-MM-DD';

interface FreeBetsFormPropsType {
    permisionsAllowed: boolean;
    customerId: number;
    freebetCreditsTotalAmount: Amount;
    currency: CurrencyType;
    submitCallback: () => void;
}

export const FreeBetsForm = observer(
    'FreeBetsFrom',
    ({ customerId, freebetCreditsTotalAmount, permisionsAllowed, currency, submitCallback }: FreeBetsFormPropsType) => {
        const { sdkStaff, reduxAction } = useAppStateContext();
        const common = useCommon();
        const [state] = useState(
            () => new FreeBetsFormState(sdkStaff, permisionsAllowed, customerId, reduxAction, submitCallback, common)
        );

        return (
            <div>
                <h3>Add Free Bet Credit</h3>

                <Input
                    dataTest='freebet-amount-input'
                    formInputState={state.amount}
                    onChange={state.onChange}
                    label='Amount'
                    placeholder='Amount'
                />

                <ButtonContainer>
                    <PaymentAdditionsNew
                        value={state.amount.value}
                        currency={currency}
                        onChange={state.handleSetAmount}
                        handleActionOnInput={state.handleResetAmount}
                    />
                </ButtonContainer>

                <Input
                    dataTest='freebet-expires-input'
                    formInputState={state.expiryDate}
                    label='Expires'
                    placeholder='yyyy-mm-dd'
                    inputType='date'
                    min={moment().add(1, 'day').format(defaultDateFormat)}
                />

                <ButtonContainer>
                    <DateAdditions
                        currDate={state.expiryDate.value}
                        onChange={state.handleCalendarChange}
                        handleClearInput={state.handleCalendarReset}
                    />
                </ButtonContainer>

                <Textarea
                    dataTest='freebet-description'
                    formState={state.promotionId}
                    label='Description'
                />

                {state.successMessage === null ? null : (
                    <SuccessMessage data-test='success-message'>{state.successMessage}</SuccessMessage>
                )}
                {state.errorMessage === null ? null : <ErrorMessage>{state.errorMessage}</ErrorMessage>}

                {permisionsAllowed === true ? null : (
                    <ErrorMessage> You don't have enough permissions to edit this</ErrorMessage>
                )}
                <table className='freebet-adjustment-form__summary'>
                    <tbody>
                        <tr data-test='free-bet-adjustment-form-credits-balance'>
                            <th>Free Bet Credits Balance:</th>
                            <td>
                                <Icon name='gift' /> {formatAmountWithCurrency(currency, freebetCreditsTotalAmount)}
                            </td>
                        </tr>
                        <tr data-test='free-bet-adjustment-form-new-balance'>
                            <th>New Balance:</th>
                            <td>
                                <Icon name='gift' />{' '}
                                {formatAmountWithCurrency(currency, state.getNewBalance(freebetCreditsTotalAmount))}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <ButtonContiner>
                    <button
                        data-test='submit-button'
                        className='btn'
                        type='submit'
                        value='clear'
                        disabled={!permisionsAllowed}
                        onClick={state.handleSubmit}
                    >
                        Submit
                    </button>
                </ButtonContiner>
            </div>
        );
    }
);
